import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { queryKeys } from '@/hooks/queries';
import type { User } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

export const useReportCount = (user: User | null) => {
  const queryClient = useQueryClient();

  const isEligible =
    user !== null && !!user.id && (user.is_admin === true || user.role === 'admin');

  const { data: pendingReportCount = 0 } = useQuery({
    queryKey: isEligible
      ? queryKeys.admin.reports.pendingCount(user.id)
      : ['reports', 'pending', 'disabled'],
    queryFn: async () => {
      if (!isEligible) return 0;

      const { count, error } = await supabase
        .from('reports')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (error) {
        return 0;
      }

      return count ?? 0;
    },
    enabled: !!isEligible,
    staleTime: 1000 * 60 * 1, // 1 minute
    refetchInterval: 1000 * 60 * 2, // Refetch every 2 minutes
  });

  useEffect(() => {
    if (!isEligible || !user.id) return;

    const channel = supabase
      .channel('report-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reports',
        },
        () => {
          queryClient
            .invalidateQueries({
              queryKey: queryKeys.admin.reports.pendingCount(user.id),
            })
            .catch(() => {
              // Query invalidation error handling
            });
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user?.id, isEligible, queryClient]);

  return pendingReportCount;
};
